// ════════════════════════════════════════════════════════════════════════════
// components/CRM/ProspectoFormModal.jsx
// Formulario optimizado de prospecto (sin lag al escribir)
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useFiscal } from '../../../hooks/useFiscal';

const ProspectoFormModal = memo(({ 
  open, 
  onClose, 
  onSubmit, 
  editandoId, 
  initialData 
}) => {
  const fiscal = useFiscal();
  // Estado LOCAL del formulario (no afecta al componente padre)
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    telefono: '',
    email: '',
    direccion: '',
    rnc_cedula: '',
    fuente: 'llamada',
    proyecto_tipo: 'residencial',
    presupuesto_estimado: ''
  });

  // Cargar datos solo cuando cambia initialData
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        nombre: '',
        empresa: '',
        telefono: '',
        email: '',
        direccion: '',
        rnc_cedula: '',
        fuente: 'llamada',
        proyecto_tipo: 'residencial',
        presupuesto_estimado: ''
      });
    }
  }, [initialData, open]);

  // Handler optimizado (no se recrea en cada render)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      keepMounted={false}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#667eea',
        color: 'white'
      }}>
        {editandoId ? '✏️ Editar Prospecto' : '➕ Nuevo Prospecto'}
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Nombre */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Nombre del contacto"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                autoFocus
              />
            </Grid>

            {/* Empresa */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Empresa"
                name="empresa"
                value={formData.empresa}
                onChange={handleChange}
              />
            </Grid>

            {/* Teléfono */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="809-555-5555"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contacto@empresa.com"
              />
            </Grid>

            {/* RNC/Cédula */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${fiscal.id_fiscal_empresa_label || 'RNC'} / ${fiscal.id_fiscal_persona_label || 'Cédula'}`}
                name="rnc_cedula"
                value={formData.rnc_cedula}
                onChange={handleChange}
                placeholder="001-1234567-8"
              />
            </Grid>

            {/* Fuente */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="Fuente"
                name="fuente"
                value={formData.fuente}
                onChange={handleChange}
              >
                <MenuItem value="web">🌐 Web</MenuItem>
                <MenuItem value="referencia">👥 Referencia</MenuItem>
                <MenuItem value="feria">🎪 Feria</MenuItem>
                <MenuItem value="llamada">📞 Llamada</MenuItem>
                <MenuItem value="visita">🏢 Visita</MenuItem>
                <MenuItem value="redes_sociales">📱 Redes Sociales</MenuItem>
                <MenuItem value="email_marketing">📧 Email Marketing</MenuItem>
              </TextField>
            </Grid>

            {/* Tipo de Proyecto */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="Tipo de Proyecto"
                name="proyecto_tipo"
                value={formData.proyecto_tipo}
                onChange={handleChange}
              >
                <MenuItem value="residencial">🏠 Residencial</MenuItem>
                <MenuItem value="comercial">🏢 Comercial</MenuItem>
                <MenuItem value="industrial">🏭 Industrial</MenuItem>
                <MenuItem value="remodelacion">🔨 Remodelación</MenuItem>
                <MenuItem value="infraestructura">🛣️ Infraestructura</MenuItem>
              </TextField>
            </Grid>

            {/* Presupuesto Estimado */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Presupuesto Estimado"
                name="presupuesto_estimado"
                value={formData.presupuesto_estimado}
                onChange={handleChange}
                placeholder="500000"
                InputProps={{
                  startAdornment: <Box sx={{ mr: 1 }}>RD$</Box>
                }}
              />
            </Grid>

            {/* Dirección */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Dirección"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Calle, sector, ciudad..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            sx={{ 
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5568d3' }
            }}
          >
            {editandoId ? 'Actualizar' : 'Crear Prospecto'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
});

ProspectoFormModal.displayName = 'ProspectoFormModal';

export default ProspectoFormModal;